import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import cm from 'classnames';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { Tree } from 'ksnm-common-ui/lib/Tree';
import { cloneDeep } from 'lodash-es';
import type { Key } from 'react';

import { DesignLayout } from '@common/DesignLayout';
import { getDataList } from '@src/utils/tree';
import { auditStore } from '@stores/auditStore';
import { reportingFormsStore } from '@stores/reportingFormsStore';
import { reportingTreeStore } from '@stores/reportingTreeStore';

import { filterTree } from '../PreparationOfReports/PublicationRegister/utils';
import { ReportingForms } from '../ReportingForms';
import { Filter } from './Filter';
import { useBgColorSelectNode } from './hooks/useBgColorSelectNode';
import { useCheckOrganization } from './hooks/useCheckOrganization';
import { useExpandedTreeFromAnotherRouter } from './hooks/useExpandedTreeFromAnotherRouter';
import { useIsTreeOpenInNewTab } from './hooks/useIsTreeOpenInNewTab';
import { useMark } from './hooks/useMark';
import { useRowClick } from './hooks/useRowClick';
import { useSessionStorageConfig } from './hooks/useSessionStorageConfig';
import { Search } from './Search';

import styles from './ReportingTree.module.scss';

const ReportingTreeComponent = (): JSX.Element => {
  const [dataList, setDataList] = useState<{ key: Key; title: string }[]>([]);

  const { removePage, pagesParam, fullSizeInfo, setFullSizeInfo } = reportingFormsStore;
  const { reportingTree, clear, stateTree, setStateTree, filters, fetch, organizationCode } = reportingTreeStore;

  useEffect(() => {
    if (organizationCode) fetch(organizationCode);
  }, [fetch, organizationCode]);

  useEffect(
    () => () => {
      removePage(true);
      setFullSizeInfo(false);
      clear();
    },
    [clear, removePage, setFullSizeInfo],
  );

  const treeData = useMemo(() => {
    let output = cloneDeep(reportingTree);

    if (filters.year) {
      output = filterTree(filters.year, output);
    }

    if (filters.group) {
      output = filterTree(filters.group, output);
    }

    if (filters.period) {
      output = filterTree(filters.period.split(' - ')[0], output, 'periodCode');
    }

    if (filters.package) {
      output = filterTree(filters.package, output);
    }

    return output;
  }, [filters.group, filters.package, filters.period, filters.year, reportingTree]);

  const loop = useMark();
  const onSelect = useRowClick(treeData);

  useCheckOrganization();
  useExpandedTreeFromAnotherRouter(onSelect);
  useSessionStorageConfig();
  useIsTreeOpenInNewTab();
  useBgColorSelectNode();

  useEffect(() => {
    auditStore.createAuditData({
      moduleName: 'Calculator',
      serviceName: 'Reporting_structure',
      operationName: 'View_reporting_tree',
      message: 'Просмотр списка отчётов',
    });
  }, []);

  useEffect(() => {
    if (treeData.length > 0) {
      setDataList(getDataList(treeData));
    }
  }, [treeData]);

  const onExpand = useCallback(
    (expandedKeys) => {
      setStateTree({ expandedKeys, autoExpandParent: false });
    },
    [setStateTree],
  );

  return (
    <>
      <DesignLayout scrollEnable>
        <Filter />

        <div className={styles.container}>
          <div className={fullSizeInfo ? styles.hide : styles.treeContainer}>
            <Search dataList={dataList} />

            <div className={styles.scrollTree}>
              {reportingTree.length > 0 && (
                <Tree
                  selectable
                  onSelect={onSelect}
                  treeData={loop(treeData)}
                  className={styles.tree}
                  onExpand={onExpand}
                  expandedKeys={stateTree.expandedKeys}
                  autoExpandParent={stateTree.autoExpandParent}
                  switcherIcon={<Icon name="expandArrow" />}
                  search={false}
                  showExpandButton={false}
                />
              )}
            </div>
          </div>

          <div
            className={
              pagesParam.length > 0
                ? cm(styles.reportingTreeInfoContainer, { [styles.fullSize]: fullSizeInfo })
                : styles.reportingTreeInfoContainerNoVisible
            }
          >
            <ReportingForms />
          </div>
        </div>
      </DesignLayout>
    </>
  );
};

export const ReportingTree = observer(ReportingTreeComponent);
